import * as React from 'react'
import clsx from 'clsx'
import { sx as sxFunc, SxProps } from '../sx'
import styles from './index.module.scss'

type Elevation = 0 | 1 | 2 | 3 | 4 | 5

export interface PaperClasses {
  /** Styles applied to the root element. */
  root: string
  /** Styles applied to the root element unless `square={true}`. */
  rounded: string
  /** Styles applied to the root element if `variant="outlined"`. */
  outlined: string
  /** Styles applied to the root element if `variant="elevation"`. */
  elevation: string
  elevation0: string
  elevation1: string
  elevation2: string
  elevation3: string
  elevation4: string
  elevation5: string
}

export interface PaperProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * The content of the component.
   */
  children?: React.ReactNode
  /**
   * The component used for the root node.
   * Either a string to use a HTML element or a component.
   * @default div
   */
  Component?: React.ElementType
  /**
   * Override or extend the styles applied to the component.
   */
  classes?: Partial<PaperClasses>
  /**
   * Shadow depth, corresponds to `px` in the spec.
   * It accepts values between 0 and 5 inclusive.
   * @default 1
   */
  elevation?: Elevation
  /**
   * If `true`, rounded corners are disabled.
   * @default false
   */
  square?: boolean
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles.
   */
  sx?: SxProps
  /**
   * The variant to use.
   * @default 'elevation'
   */
  variant?: 'elevation' | 'outlined'
}

export const Paper = React.forwardRef<HTMLDivElement, PaperProps>(
  function Paper(props, ref) {
    const {
      className,
      classes,
      Component = 'div',
      elevation = 1,
      square = false,
      variant = 'elevation',
      sx,
      ...other
    } = props

    return (
      <Component
        className={clsx(
          styles.root,
          classes?.root,
          !square && [styles.rounded, classes?.rounded],
          variant === 'outlined' && [
            styles[`variant-${variant}`],
            classes?.outlined,
          ],
          variant !== 'outlined' && [
            styles[`elevation-${elevation}`],
            classes?.[`elevation${elevation}`],
          ],
          className,
          sxFunc(sx)
        )}
        ref={ref}
        {...other}
      />
    )
  }
)
